import React from "react";
import { CheckCircle, Clock, Truck, Home, MapPin } from "lucide-react";

export default function OrderTimeline({ status, createdAt, estimatedDelivery, fulfillmentType = "delivery" }) {
  const isPickup = fulfillmentType === "pickup";

  const STEPS = [
    {
      key: "confirmed",
      label: "Order Confirmed",
      icon: CheckCircle,
      desc: "Your order has been received and payment confirmed.",
    },
    {
      key: "processing",
      label: "Processing",
      icon: Clock,
      desc: "Your items are being packed and prepared for dispatch.",
    },
    {
      key: "shipped",
      label: isPickup ? "In Transit to Hub" : "Shipped",
      icon: Truck,
      desc: isPickup
        ? "Your package is on its way to the designated pickup station."
        : "Your order has been dispatched and is on its way to your address.",
    },
    {
      key: "delivered",
      label: isPickup ? "Ready for Pickup / Collected" : "Delivered",
      icon: isPickup ? MapPin : Home,
      desc: isPickup
        ? "Package arrived at pickup station. Present your order reference to collect!"
        : "Order delivered safely to your address. Enjoy your purchase!",
    },
  ];

  const ORDER_IDX = { confirmed: 0, processing: 1, shipped: 2, delivered: 3 };
  const currentIdx = ORDER_IDX[status] ?? 0;
  const isCancelled = status === "cancelled";

  if (isCancelled) {
    return (
      <div style={{ padding: "20px 24px", background: "#FEF2F2", borderRadius: 12, border: "1px solid #FECACA" }}>
        <p style={{ color: "#DC2626", fontWeight: 600, fontSize: 15 }}>❌ Order Cancelled</p>
        <p style={{ color: "#EF4444", fontSize: 14, marginTop: 4 }}>
          This order has been cancelled. Contact support if you need help.
        </p>
      </div>
    );
  }

  return (
    <div>
      {estimatedDelivery && (
        <div
          style={{
            padding: "10px 16px",
            background: "rgba(232, 71, 26, 0.06)",
            border: "1px solid rgba(232, 71, 26, 0.15)",
            borderRadius: 10,
            marginBottom: 20,
            fontSize: 13,
            display: "flex",
            alignItems: "center",
            gap: 8,
          }}
        >
          <Clock size={16} color="var(--brand)" />
          <span>
            <strong>7-Day Delivery Guarantee:</strong> Expected by <strong>{estimatedDelivery}</strong>
          </span>
        </div>
      )}
      <div className="timeline">
        {STEPS.map((step, i) => {
          const Icon = step.icon;
          const done = i < currentIdx;
          const active = i === currentIdx;
          return (
            <div key={step.key} className="timeline-item">
              <div className={`timeline-dot${done ? " done" : active ? " active" : ""}`}>
                {done && (
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
              <div style={{ opacity: done || active ? 1 : 0.4 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <Icon size={15} color={active ? "var(--brand)" : done ? "var(--success)" : "var(--text-muted)"} />
                  <span
                    style={{
                      fontWeight: active ? 700 : 600,
                      fontSize: 14,
                      color: active ? "var(--brand)" : "var(--text)",
                    }}
                  >
                    {step.label}
                  </span>
                  {active && <span className="badge badge-info" style={{ fontSize: 11 }}>Current</span>}
                  {done && <span className="badge badge-success" style={{ fontSize: 11 }}>Done</span>}
                </div>
                <p style={{ fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>{step.desc}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
